// Website copy + live company data for the information pages (worlds, Stage, Yasmin, travel, business, legal…).
// Static copy comes from site.json (exported by website/export_content.py from the same sources the app uses);
// live data (plans, blog, roles, company details, waitlist) comes from the shared backend.
import { useEffect, useState } from "react";
import SITE from "@/content/site.json";
import { api } from "@/lib/api";

export { SITE };

export const siteApi = {
  plans: () => api("/plans", { auth: false }),
  blog: () => api("/blog", { auth: false }),
  post: (slug) => api(`/blog/${encodeURIComponent(slug)}`, { auth: false }),
  careers: () => api("/careers", { auth: false }),
  company: () => api("/company-details", { auth: false }),
  waitlist: (body) => api("/launch/waitlist", { method: "POST", auth: false, body }),
};

const DEFAULT_DESC = "Fable + — one account, six worlds. Book trusted local pros, experiences, food, tutors, rentals and casting across the UK.";

/** Sets the tab title and meta description for an information page. */
export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title ? `${title} · Fable +` : "Fable + — one account, six worlds";
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.name = "description";
      document.head.appendChild(tag);
    }
    tag.content = description || DEFAULT_DESC;
  }, [title, description]);
}

/** Fetch once; returns undefined while loading, null on failure. */
export function useLive(fn, deps = []) {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let alive = true;
    fn()
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return data;
}

let companyCache;
export function useCompany() {
  const [c, setC] = useState(companyCache);
  useEffect(() => {
    if (companyCache) return;
    siteApi
      .company()
      .then((d) => {
        companyCache = d;
        setC(d);
      })
      .catch(() => setC(null));
  }, []);
  return c;
}

const real = (v) => (v && !/coming soon/i.test(String(v)) ? v : "");

/** "Fable + is operated by Mamour Media Ltd, registered in England and Wales (no. …). Registered office: …" */
export function companyLine(c) {
  if (!c) return `Fable + is operated by ${SITE.company}.`;
  const bits = [`${c.trading_as || "Fable +"} is operated by ${c.legal_name || SITE.company}`];
  if (real(c.trade_registration_no)) bits[0] += `, a company registered in England and Wales (no. ${c.trade_registration_no})`;
  if (real(c.registered_office)) bits.push(`Registered office: ${c.registered_office}`);
  if (real(c.vat_id)) bits.push(`VAT no. ${c.vat_id}`);
  return bits.join(". ") + ".";
}

export const worldByKey = (key) => SITE.worlds.find((w) => w.key === key);
export const cityBySlug = (slug) => SITE.cities.find((c) => c.slug === slug);

export function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return Number.isInteger(n) ? `£${n}` : `£${n.toFixed(2)}`;
}

/** Aviasales search link (same format as the app and the old site; affiliate wrapping happens server-side in the app). */
export function flightUrl({ from, to, out, back, pax }) {
  const dm = (s) => s.slice(8, 10) + s.slice(5, 7);
  return `https://www.aviasales.com/search/${from}${dm(out)}${to}${back ? dm(back) : ""}${pax || 1}`;
}
